import { post, getBlob } from '../../util/http'
import { denormalizeEntity } from './entities'
import * as schema from '../schema'
import {fromJS, List, Set} from 'immutable'
import uniqueId from 'lodash/uniqueId'

/**
  This reducer is necessary to actually control our content. So for example,
  when a user hits continue in content or answers a question to move on.

  The rendered content is also loaded here.

  If you actually want to load that content for the user, it needs to be done through here.
  Don't be tempted by content appearing in other places, for example
  topic['explanations'].

  
*/


/**
 * Actions
 */
export const LOAD_CONTENT = 'expii/content/LOAD_CONTENT'
export const INPUT_ANSWER = 'expii/content/INPUT_ANSWER'
export const REVEAL_ANSWER = 'expii/content/REVEAL_ANSWER'
export const SUBMIT_ANSWER = 'expii/content/SUBMIT_ANSWER'
export const PAUSE_CONTINUE = 'expii/content/PAUSE_CONTINUE'
export const TOGGLE_VOTE = 'expii/content/TOGGLE_VOTE'
export const LOAD_CONTENT_ERROR = 'expii/content/LOAD_CONTENT_ERROR'
export const CLEAR_CONTENT = 'expii/content/CLEAR_CONTENT'

/**
 * Reducer
 */
const defaultContentState = fromJS({
  entityName: null, //'explanation' or 'problem'
  activeEntity: {},
  entityExists: true,
  isLoading: true
})


export default function reducer(state = defaultContentState, action = {}) {
  let content = null
  let shouldContinue = false
  switch (action.type) {
  case LOAD_CONTENT:
    return state.merge(
      fromJS({
        entityName: action.entityName,
        activeEntity: action.activeEntity,
        entityExists: true,
        isLoading: false
      })
    )
  case LOAD_CONTENT_ERROR:
    return state.withMutations( map =>
      map.set('entityName', null)
        .set('activeEntity', { error: 'Invalid id' })
        .set('entityExists', false)
    )
  case INPUT_ANSWER:
    content = state.getIn([
      'activeEntity',
      'renderedContent',
      action.blockIndex,
      'content'
    ])
    if (
      content.get('answerType') === 'text_entry' ||
        content.get('answerType') === 'multiple_choice'
    ) {
      content = content.set('givenAnswer', List([action.answer]))
    } else {
      let givenAnswer = content.get('givenAnswer')
      const answerIndex = givenAnswer.indexOf(action.answer)
      if (answerIndex !== -1) {
        givenAnswer = givenAnswer.delete(answerIndex)
      } else {
        givenAnswer = givenAnswer.push(action.answer)
      }
      content = content.set('givenAnswer', givenAnswer)
    }
    content = content.set('hasSubmitted', false)
    return state.setIn(
      ['activeEntity', 'renderedContent', action.blockIndex, 'content'],
      content
    )
  case SUBMIT_ANSWER:
    content = state.getIn([
      'activeEntity',
      'renderedContent',
      action.blockIndex,
      'content'
    ])
    const givenAnswer = Set(content.get('givenAnswer'))
    shouldContinue = content.get('shouldContinue')
    let isCorrect = false

    if (content.get('answerType') !== 'text_entry') {
      const correctAnswer = Set(
        content
          .get('answers')
          .filter(a => a.get('correct'))
          .map(a => a.get('_id'))
      )
      if (correctAnswer.equals(givenAnswer)) {
        shouldContinue = true
        isCorrect = true
      }
    } else {
      const unrenderedContent = state.getIn([
        'activeEntity',
        'content',
        action.blockIndex,
        'content'
      ])
      const correctAnswers = Set(
        unrenderedContent
          .get('answers')
          .map(a => a.getIn(['value', 0, 'text']))
      )
      if (correctAnswers.intersect(givenAnswer).size) {
        shouldContinue = true
        isCorrect = true
      }
    }
    content = content.merge(
      fromJS({
        shouldContinue,
        isCorrect,
        hasSubmitted: true
      })
    )
    return state.setIn(
      ['activeEntity', 'renderedContent', action.blockIndex, 'content'],
      content
    )
  case REVEAL_ANSWER:
    content = state.getIn([
      'activeEntity',
      'renderedContent',
      action.blockIndex,
      'content'
    ])
    if (content.get('answerType') !== 'text_entry') {
      content = content.set(
        'givenAnswer',
        content
          .get('answers')
          .filter(a => a.get('correct'))
          .map(a => a.get('_id'))
      )
    } else {
      const unrenderedContentAnswer = state.getIn([
        'activeEntity',
        'content',
        action.blockIndex,
        'content',
        'answers',
        0,
        'value',
        0,
        'text'
      ])
      content = content.set(
        'givenAnswer',
        List([unrenderedContentAnswer])
      )
    }

    content = content.merge(
      fromJS({
        shouldContinue: true,
        isCorrect: true,
        hasSubmitted: true
      })
    )
    return state.setIn(
      ['activeEntity', 'renderedContent', action.blockIndex, 'content'],
      content
    )
  case PAUSE_CONTINUE:
    content = state.getIn([
      'activeEntity',
      'renderedContent',
      action.blockIndex,
      'content'
    ])
    shouldContinue = true
    content = content.merge(
      fromJS({
        shouldContinue
      })
    )
    return state.setIn(
      ['activeEntity', 'renderedContent', action.blockIndex, 'content'],
      content
    )
  case TOGGLE_VOTE:
    const { currentUserHasVoted, voteCount } = action.payload
    return state.withMutations(map => {
      map
        .setIn(['activeEntity', 'currentUserHasVoted'], currentUserHasVoted)
        .setIn(['activeEntity', 'voteCount'], voteCount)
    })
  case CLEAR_CONTENT:
    return defaultContentState
  default:
    return state
  }
}

/**
 * Action Creators
 */
export function loadContent(entityName, activeEntity) {
  return { type: LOAD_CONTENT, entityName, activeEntity }
}
export function loadContentError() {
  return { type: LOAD_CONTENT_ERROR }
}
export function inputAnswer(blockIndex, answer, answerType) {
  return { type: INPUT_ANSWER, blockIndex, answer, answerType }
}
export function submitAnswer(blockIndex) {
  return { type: SUBMIT_ANSWER, blockIndex }
}
export function revealAnswer(blockIndex) {
  return { type: REVEAL_ANSWER, blockIndex }
}
export function pauseContinue(blockIndex) {
  return { type: PAUSE_CONTINUE, blockIndex }
}
export function toggleVote(voteResults) {
  return { type: TOGGLE_VOTE, payload: voteResults }
}
export function clearContent() {
  return {type: CLEAR_CONTENT }
}

/**
 * Side Effects
 */

/**
 * toggles user's vote on contentType
 * @param  {string} type content model type
 * @param  {number} id   content id
 */
export function postToggleVote(type, id) {
  return (dispatch, getState) => {
    return post(`/api/content/toggle_vote`, { type, id }).then(json => {
      let { success, output } = json
      json = {
        success: success,
        type,
        id,
        currentUserHasVoted: output.currentUserHasVoted,
        voteCount: output.voteCount
      }
      dispatch(toggleVote(json))
    })
  }
}
function processRenderedContent(c) {
  if (c.get('type') === 'ask') {
    let innerContent = c.get('content')
    let answers = innerContent.get('answers').map((a) => {
      if (!a.has('_id')) {
        a = a.set('_id', uniqueId('answer'))
      }
      return a
    })
    innerContent = innerContent.set('answers', answers)
    innerContent = innerContent.merge(
      fromJS({
        shouldContinue: false,
        hasSubmitted: false,
        isCorrect: false,
        givenAnswer: []
      })
    )
    return c.set('content', innerContent)
  }
  if (c.get('type') === 'pause') {
    return c.merge(
      fromJS({ content: { shouldContinue: false } })
    )
  }
  return c
}

export function loadContentEntity(entityName, contentId) {
  return (dispatch, getState) => {
    const activeEntity = denormalizeEntity(
      getState(),
      contentId.toString(),
      schema[entityName]
    )
    // Error if Nonexistent Entity
    if (activeEntity == null) {
      dispatch(loadContentError())
      return
    }
    const renderedContent = activeEntity.has('renderedContent') ? activeEntity.get('renderedContent') : null
    const renderedContentUrl = activeEntity.get('renderedContentUrl')
    if (renderedContent) {
      const content = activeEntity.get('renderedContent').map(processRenderedContent)
      const modifiedEntity = activeEntity.set('renderedContent', content)
      dispatch(loadContent(entityName, modifiedEntity))
    } else {
      dispatch(clearContent())
      getBlob(renderedContentUrl).then((renderedContent)=>{
        const content = fromJS(renderedContent).map(processRenderedContent)
        const modifiedEntity = activeEntity.set('renderedContent', content)
        dispatch(loadContent(entityName, modifiedEntity))
      })
    }
  }
}

/**
 * Selectors
 */

export const selectActiveContent = (state, key) => {
  return state.getIn(['contentCollection', key, 'activeEntity'])
}

export const selectEntityExists = (state, key) => {
  return state.getIn(['contentCollection', key, 'entityExists'])
}

export const selectEntityIsLoading = (state, key) => {
  return state.getIn(['contentCollection', key, 'isLoading'])
}



// WEBPACK FOOTER //
// ./src/redux/modules/content.js